import { parseAddressParts, stripLeadingStreetNumber } from './pricingPath';
import { getLocationStateByCode } from '../config/locationStates';

const DEFAULT_SUPPORT_PHONE = '';

const INTRO_TEMPLATES = [
  '{areaLabel} gives drivers a direct option for truck parking in {city}, {stateCode}. This lot supports predictable stop planning with route access near {highwayOrCorridor}, practical security controls, and online booking 24/7.',
  'For drivers moving through {city}, {stateCode}, {areaLabel} provides secure truck parking with consistent access around {highwayOrCorridor}. Use online booking, arrive within your booking window, and keep dispatch timing stable.',
  'This truck parking location in {city}, {stateCode} is positioned for dependable route flow near {highwayOrCorridor}. {areaLabel} combines straightforward entry rules, visible lot controls, and booking support any time.'
];

const FALLBACK_FREIGHT_HOTSPOT_TEMPLATES = [
  { name: '{city} warehouse district', type: 'warehouse area' },
  { name: '{city} distribution zones', type: 'distribution center' },
  { name: '{city} delivery corridors', type: 'delivery zone' },
  { name: '{stateCode} industrial corridors', type: 'industrial area' },
  { name: '{areaLabel} logistics approaches', type: 'freight access zone' },
];

const FAQ_TEMPLATES = [
  {
    question: 'Is overnight truck parking allowed at this location?',
    answer: 'Overnight stays are supported based on selected duration and active booking confirmation. Drivers should keep booking details ready at arrival.'
  },
  {
    question: 'What trailer or unit types are accepted here?',
    answer: 'The location supports common commercial unit configurations. Drivers should follow posted entry notes and choose the booking option that matches their equipment.'
  },
  {
    question: 'Can I reserve same-day truck parking?',
    answer: 'Yes. Same-day online reservation is available when spots are open. Availability updates throughout the day as inventory changes.'
  },
  {
    question: 'Are in-and-out privileges included during a booking?',
    answer: 'Yes. Drivers can leave and return during an active reservation, as long as re-entry happens within the booked time window.'
  },
  {
    question: 'What do I need at check-in?',
    answer: 'Keep your active reservation confirmation (email or booking details page) ready. For gated lots, use the gate code provided with your booking.'
  },
  {
    question: 'How does "As low as" monthly pricing work?',
    answer: 'Monthly and no-contract pricing may display the lowest available vehicle-tier rate at that lot. Final checkout pricing updates by selected vehicle type, so truck-only, trailer-only, and truck-and-trailer pricing can differ at the same location.'
  },
  {
    question: 'Why do I need to talk to your team for monthly contract parking?',
    answer: 'Start monthly parking through the app or lot booking page so you can select the location and submit your request directly in the platform. If anything needs review, our team can follow up after your in-app request to confirm vehicle type, availability, and setup details.'
  },
  {
    question: 'Is the lot monitored after dark?',
    answer: 'Lighting and monitoring controls are active based on location configuration, with security features listed on the page for this lot.'
  },
];

const SECURITY_FLAG_LABELS = [
  ['fenced', 'Perimeter fencing'],
  ['securityCameras', 'Camera coverage'],
  ['onSiteOffice', 'On-site operations presence'],
  ['lights', 'Night lighting'],
  ['gateAccess', 'Controlled gate access'],
  ['gateOpen', 'Managed gate open/close controls'],
  ['gateSlideIn', 'Slide-in gate entry controls'],
];

const AMENITY_FLAG_LABELS = [
  ['bathroom', 'Restrooms'],
  ['restroomShowers', 'Showers'],
  ['nearGas', 'Fuel nearby'],
  ['fuelNearby', 'Fuel nearby'],
  ['nearFood', 'Food options nearby'],
  ['nearHotels', 'Lodging nearby'],
  ['serviceAvailable', 'Service support nearby'],
  ['access24h', '24/7 access'],
  ['paved', 'Paved surface'],
  ['gravel', 'Gravel surface'],
];

const ACCESS_CHECK_IN_DEFAULTS = [
  'Keep your active reservation confirmation ready before arriving.',
  'Use the gate code from your booking details when the lot uses gated entry.'
];

const LARGE_RIG_DEFAULTS = [
  'Use wide-turn approach and reduce speed entering from main road frontage.',
  'Stage alignment before gate approach to avoid blocking inbound/outbound lanes.',
  'Follow lot-specific turning markers where present for trailer clearance.'
];

const ENTRY_EXIT_DEFAULTS = [
  'Entry and exit are allowed during your reserved booking window.'
];

const DRIVER_REQUIREMENT_DEFAULTS = [
  'Valid reservation confirmation (email or booking details page).',
];

const toStringSafe = (value = '') => String(value || '').trim();

const normalizeLineList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => toStringSafe(entry))
    .filter(Boolean);
};

const hashSeed = (seed = '') => {
  const source = toStringSafe(seed) || 'rstc';
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = ((hash << 5) - hash) + source.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const pickBySeed = (items = [], seedValue = '', salt = 0) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  const index = (hashSeed(`${seedValue}:${salt}`) % items.length + items.length) % items.length;
  return items[index];
};

const pickDistinctBySeed = (items = [], count = 1, seedValue = '') => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const maxCount = Math.max(0, Math.min(count, items.length));
  const selected = [];
  const used = new Set();
  let salt = 0;
  while (selected.length < maxCount && salt < items.length * 4) {
    const candidate = pickBySeed(items, seedValue, salt);
    const key = JSON.stringify(candidate);
    if (!used.has(key)) {
      used.add(key);
      selected.push(candidate);
    }
    salt += 1;
  }
  return selected;
};

const resolveCityState = (location = null) => {
  const parsed = parseAddressParts(location?.address || '');
  const city = toStringSafe(parsed.city || location?.city || '');
  const stateCode = toStringSafe(parsed.stateCode || location?.state || '').toUpperCase();
  const stateMeta = getLocationStateByCode(stateCode);

  return {
    city: city || 'this area',
    stateCode: stateCode || 'US',
    stateName: stateMeta?.name || stateCode || 'United States',
    street: toStringSafe(parsed.street || ''),
  };
};

const deriveAreaLabel = (location = null, cityState = null) => {
  const profileLabel = toStringSafe(location?.seoProfile?.areaLabel || '');
  if (profileLabel) return profileLabel;

  const streetBase = stripLeadingStreetNumber(cityState?.street || '') || cityState?.street;
  if (streetBase) return streetBase;

  return toStringSafe(location?.title || 'Main Access Corridor');
};

const ROUTE_TOKEN_PATTERN = /\b(?:I[-\s]?\d{1,3}|US[-\s]?\d{1,3}|SR[-\s]?\d{1,3}|NC[-\s]?\d{1,3}|SC[-\s]?\d{1,3}|GA[-\s]?\d{1,3})\b/gi;

const normalizeRouteToken = (token = '') => {
  const raw = toStringSafe(token).toUpperCase();
  if (!raw) return '';
  if (raw.startsWith('I ')) return `I-${raw.slice(2).trim()}`;
  if (raw.startsWith('US ')) return `US-${raw.slice(3).trim()}`;
  if (raw.startsWith('SR ')) return `SR-${raw.slice(3).trim()}`;
  if (raw.startsWith('NC ')) return `NC-${raw.slice(3).trim()}`;
  if (raw.startsWith('SC ')) return `SC-${raw.slice(3).trim()}`;
  if (raw.startsWith('GA ')) return `GA-${raw.slice(3).trim()}`;
  return raw.replace(/\s+/g, '-');
};

const extractRouteTokens = (...sources) => {
  const joined = sources
    .map((source) => toStringSafe(source))
    .filter(Boolean)
    .join(' ');

  if (!joined) return [];

  const matches = joined.match(ROUTE_TOKEN_PATTERN) || [];
  const dedupe = new Set();
  matches.forEach((match) => {
    const normalized = normalizeRouteToken(match);
    if (normalized) dedupe.add(normalized);
  });
  return [...dedupe];
};

const normalizeHighways = (value = []) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const name = toStringSafe(entry.name || '');
      if (!name) return null;
      const exit = toStringSafe(entry.exit || '');
      const minutesRaw = Number(entry.minutes);
      const minutes = Number.isFinite(minutesRaw) && minutesRaw > 0 ? Math.round(minutesRaw) : null;
      return {
        name,
        exit: exit || undefined,
        minutes: minutes || undefined,
      };
    })
    .filter(Boolean);
};

const buildFallbackHighways = ({ location, cityState, areaLabel }) => {
  const extractedRoutes = extractRouteTokens(
    location?.address,
    location?.title,
    areaLabel,
    cityState?.street
  );

  if (extractedRoutes.length > 0) {
    return extractedRoutes.slice(0, 3).map((name) => ({ name }));
  }

  return [
    `Primary freight corridors around ${cityState.city}`,
    `${cityState.stateCode} regional trucking routes`,
  ].map((name) => ({ name }));
};

const normalizeFreightHotspots = (value = []) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const name = toStringSafe(entry.name || '');
      if (!name) return null;
      const type = toStringSafe(entry.type || '');
      const minutesRaw = Number(entry.minutes);
      const minutes = Number.isFinite(minutesRaw) && minutesRaw > 0 ? Math.round(minutesRaw) : null;
      return {
        name,
        type: type || undefined,
        minutes: minutes || undefined,
      };
    })
    .filter(Boolean);
};

const buildFallbackFreightHotspots = ({ cityState, areaLabel }) => {
  const templates = FALLBACK_FREIGHT_HOTSPOT_TEMPLATES.map((template) => ({
    name: formatTemplate(template.name, {
      city: cityState.city,
      stateCode: cityState.stateCode,
      areaLabel,
    }),
    type: template.type,
  }));

  return templates.slice(0, 3);
};

const normalizeFaq = (value = []) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const question = toStringSafe(entry.question || '');
      const answer = toStringSafe(entry.answer || '');
      if (!question || !answer) return null;
      const actionLabel = toStringSafe(entry.actionLabel || '');
      const actionPath = toStringSafe(entry.actionPath || '');
      return {
        question,
        answer,
        ...(actionLabel && actionPath ? { actionLabel, actionPath } : {}),
      };
    })
    .filter(Boolean);
};

const PRICING_MECHANISM_FAQ = {
  question: 'How does "As low as" monthly pricing work?',
  answer: 'Monthly and no-contract pricing may display the lowest available vehicle-tier rate at that lot. Final checkout pricing updates by selected vehicle type, so truck-only, trailer-only, and truck-and-trailer pricing can differ at the same location.',
};

const PRICING_MECHANISM_FAQ_PATTERN = /as low as|vehicle-tier rate|selected vehicle type|truck-only|truck-and-trailer/i;

const MONTHLY_CONTRACT_TEAM_FAQ = {
  question: 'Why do I need to talk to your team for monthly contract parking?',
  answer: 'Start monthly parking through the app or lot booking page so you can select the location and submit your request directly in the platform. If anything needs review, our team can follow up after your in-app request to confirm vehicle type, availability, and setup details.',
  actionLabel: 'Manage Booking',
  actionPath: '/manage-booking',
};

const MONTHLY_CONTRACT_TEAM_FAQ_PATTERN = /monthly contract parking|talk to your team|account setup details|before activation/i;

const ensurePricingMechanismFaq = (faqEntries = []) => {
  const normalizedEntries = normalizeFaq(faqEntries);
  const hasPricingMechanismFaq = normalizedEntries.some((entry) => (
    PRICING_MECHANISM_FAQ_PATTERN.test(`${entry.question} ${entry.answer}`)
  ));
  const hasMonthlyContractTeamFaq = normalizedEntries.some((entry) => (
    MONTHLY_CONTRACT_TEAM_FAQ_PATTERN.test(`${entry.question} ${entry.answer}`)
  ));

  if (hasPricingMechanismFaq && hasMonthlyContractTeamFaq) {
    return normalizedEntries.slice(0, 6);
  }

  const requiredEntries = [
    ...(hasPricingMechanismFaq ? [] : [PRICING_MECHANISM_FAQ]),
    ...(hasMonthlyContractTeamFaq ? [] : [MONTHLY_CONTRACT_TEAM_FAQ]),
  ];

  const preservedExistingCount = Math.max(0, 6 - requiredEntries.length);

  return [
    ...normalizedEntries.slice(0, preservedExistingCount),
    ...requiredEntries,
  ].slice(0, 6);
};

const buildFallbackFaq = (seedValue = '') => {
  return pickDistinctBySeed(FAQ_TEMPLATES, 4, seedValue);
};

const normalizePhotos = (value = []) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const url = toStringSafe(entry.url || '');
      const alt = toStringSafe(entry.alt || '');
      if (!url || !alt) return null;
      return { url, alt };
    })
    .filter(Boolean);
};

const getEnabledFlagLabels = (flags = {}, labelMap = []) => {
  if (!flags || typeof flags !== 'object') return [];
  const dedupe = new Set();
  labelMap.forEach(([key, label]) => {
    if (flags[key] === true) dedupe.add(label);
  });
  return [...dedupe];
};

const buildDefaultDriverRequirements = (flags = {}) => {
  const requirements = [...DRIVER_REQUIREMENT_DEFAULTS];
  if (flags.gateAccess === true || flags.gateOpen === true || flags.gateSlideIn === true) {
    requirements.unshift('Gate code required for gated-entry lots.');
  }
  return requirements;
};

const buildTopAmenities = ({ amenityLabels = [], nearbyGasStations = [], nearbyHotels = [], nearbyFood = [] } = {}) => {
  const dynamic = [...amenityLabels];

  if (Array.isArray(nearbyGasStations) && nearbyGasStations.length > 0) {
    dynamic.push('fuel options close by');
  }
  if (Array.isArray(nearbyHotels) && nearbyHotels.length > 0) {
    dynamic.push('lodging nearby');
  }
  if (Array.isArray(nearbyFood) && nearbyFood.length > 0) {
    dynamic.push('food access nearby');
  }

  if (dynamic.length === 0) {
    return 'Security-forward access and driver-focused lot operations';
  }

  const top = dynamic.slice(0, 3);
  if (top.length === 1) return top[0];
  if (top.length === 2) return `${top[0]} and ${top[1]}`;
  return `${top[0]}, ${top[1]}, and ${top[2]}`;
};

const formatTemplate = (template = '', replacements = {}) => {
  return Object.entries(replacements).reduce((result, [key, value]) => {
    return result.replaceAll(`{${key}}`, value);
  }, template);
};

const buildIntro = ({ location, areaLabel, cityState, highwayOrCorridor, seedValue }) => {
  const configured = toStringSafe(location?.seoProfile?.intro || '');
  if (configured.length >= 80) {
    return configured;
  }

  const template = pickBySeed(INTRO_TEMPLATES, seedValue, 10) || INTRO_TEMPLATES[0];
  return formatTemplate(template, {
    areaLabel,
    city: cityState.city,
    stateCode: cityState.stateCode,
    highwayOrCorridor,
  });
};

const buildMapUrl = (location = null) => {
  const lat = Number(location?.coordinates?.lat);
  const lng = Number(location?.coordinates?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  const address = toStringSafe(location?.address || '');
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

export const buildLocationSeoProfile = (location = null) => {
  const seoProfile = location?.seoProfile && typeof location.seoProfile === 'object'
    ? location.seoProfile
    : {};

  const cityState = resolveCityState(location);
  const seedValue = toStringSafe(location?.id || location?.title || location?.address || 'rstc');
  const areaLabel = deriveAreaLabel(location, cityState);

  const configuredHighways = normalizeHighways(seoProfile.highways);
  const highways = configuredHighways.length > 0
    ? configuredHighways
    : buildFallbackHighways({
      location,
      cityState,
      areaLabel,
    });

  const configuredFreight = normalizeFreightHotspots(seoProfile.freightHotspots);
  const freightHotspots = configuredFreight.length > 0
    ? configuredFreight
    : buildFallbackFreightHotspots({
      cityState,
      areaLabel,
    });

  const faq = (() => {
    const configuredFaq = normalizeFaq(seoProfile.faq);
    if (configuredFaq.length > 0) return ensurePricingMechanismFaq(configuredFaq);
    return ensurePricingMechanismFaq(buildFallbackFaq(seedValue));
  })();

  const flags = location?.flags || {};
  const securityFeatures = getEnabledFlagLabels(flags, SECURITY_FLAG_LABELS);
  const amenityLabels = getEnabledFlagLabels(flags, AMENITY_FLAG_LABELS);
  const nearbyGasStations = Array.isArray(location?.nearbyGasStations) ? location.nearbyGasStations : [];
  const nearbyHotels = Array.isArray(location?.nearbyHotels) ? location.nearbyHotels : [];
  const nearbyFood = Array.isArray(location?.nearbyFood) ? location.nearbyFood : [];

  const topAmenities = buildTopAmenities({
    amenityLabels,
    nearbyGasStations,
    nearbyHotels,
    nearbyFood,
  });

  const firstHighway = highways[0];
  const highwayOrCorridor = firstHighway
    ? `${firstHighway.name}${firstHighway.exit ? ` Exit ${firstHighway.exit}` : ''}`
    : 'major freight corridors';

  const intro = buildIntro({
    location,
    areaLabel,
    cityState,
    highwayOrCorridor,
    seedValue,
  });

  const configuredCheckIn = normalizeLineList(seoProfile.checkInInstructions).slice(0, 6);
  const configuredLargeRigNotes = normalizeLineList(seoProfile.largeRigNotes).slice(0, 6);
  const configuredEntryExitRules = normalizeLineList(seoProfile.entryExitRules).slice(0, 6);
  const configuredDriverRequirements = normalizeLineList(seoProfile.driverRequirements).slice(0, 6);

  return {
    seedValue,
    city: cityState.city,
    stateCode: cityState.stateCode,
    stateName: cityState.stateName,
    areaLabel,
    intro,
    highways,
    freightHotspots,
    checkInInstructions: configuredCheckIn.length > 0 ? configuredCheckIn : ACCESS_CHECK_IN_DEFAULTS.slice(0, 6),
    largeRigNotes: configuredLargeRigNotes.length > 0 ? configuredLargeRigNotes : LARGE_RIG_DEFAULTS.slice(0, 6),
    entryExitRules: configuredEntryExitRules.length > 0 ? configuredEntryExitRules : ENTRY_EXIT_DEFAULTS.slice(0, 6),
    driverRequirements: configuredDriverRequirements.length > 0
      ? configuredDriverRequirements
      : buildDefaultDriverRequirements(flags).slice(0, 6),
    securityFeatures,
    amenityLabels,
    nearbyGasStations,
    nearbyHotels,
    nearbyFood,
    faq,
    photos: normalizePhotos(seoProfile.photos),
    openingHours: normalizeLineList(seoProfile.openingHours),
    supportPhone: toStringSafe(seoProfile.supportPhone || location?.phone || DEFAULT_SUPPORT_PHONE),
    highwayOrCorridor,
    topAmenities,
    mapUrl: buildMapUrl(location),
  };
};

export const buildSchemaAmenityFeatures = (profile = null) => {
  const source = profile || {};
  const features = [];
  const addFeature = (name) => {
    if (!name) return;
    features.push({
      '@type': 'LocationFeatureSpecification',
      name,
      value: true,
    });
  };

  (source.securityFeatures || []).forEach(addFeature);
  (source.amenityLabels || []).forEach(addFeature);

  if ((source.nearbyGasStations || []).length > 0) addFeature('Gas station proximity');
  if ((source.nearbyHotels || []).length > 0) addFeature('Hotels nearby');
  if ((source.nearbyFood || []).length > 0) addFeature('Food nearby');

  return features;
};

export const getInternalLinkAnchorVariant = ({ seedValue = '', variants = [], salt = 0, fallback = '' } = {}) => {
  const selected = pickBySeed(variants, seedValue, salt);
  return selected || fallback;
};

export default {
  buildLocationSeoProfile,
  buildSchemaAmenityFeatures,
  getInternalLinkAnchorVariant,
};
